import { useState } from "react";

import {
  Dialog,
  DialogActionsWrapper,
} from "@/design-system/components/Dialog/Dialog";
import { Button } from "@/design-system/components/Button/Button";
import { User } from "@/components/users/types";
import { Form } from "@/forms/forms_types";

import useForms from "@/forms/queries/useForms";
import useFormSubmissionAccess from "@/access-control/queries/useFormSubmissionAccess";
import { FormAccessSelector } from "../FormAccessSelector";

type FormAccessManagementModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (formIDs: string[]) => void;
  member: User | null;
};

function commonFormIDs(formIDs: string[], forms: Form[]): string[] {
  return formIDs.filter((id) => forms.some((form) => form.id === id));
}

export function FormAccessManagementModal({
  open,
  onClose,
  onSave,
  member,
}: FormAccessManagementModalProps) {
  const { data: forms } = useForms();
  const { data: formSubmissionsAccess } = useFormSubmissionAccess(
    member?.uid ?? "",
  );
  const [selectedFormIDs, setSelectedFormIDs] = useState<string[]>([]);
  const [formIDsPopulated, setFormIDsPopulated] = useState(false);
  const [allSelected, setAllSelected] = useState(false);

  if (!member || !formSubmissionsAccess) {
    return null;
  }

  const allForms = forms ?? [];

  if (!formIDsPopulated && !!formSubmissionsAccess?.form_ids) {
    setFormIDsPopulated(true);
    setSelectedFormIDs(commonFormIDs(formSubmissionsAccess.form_ids, allForms));
  }

  return (
    <Dialog
      onOpenChange={() => {
        setFormIDsPopulated(false);
        onClose();
      }}
      open={open}
      title="Form submission permissions"
      contentProps={{
        className: "w-2xl",
      }}
    >
      <div className="flex flex-col gap-4">
        <div>
          <p className="font-bold" id="email">
            Email
          </p>
          <p>{member?.email}</p>
        </div>
        <FormAccessSelector
          onSelect={(formID) => {
            let i = selectedFormIDs.indexOf(formID);
            if (i > -1) {
              setSelectedFormIDs(selectedFormIDs.filter((f) => f !== formID));
            } else {
              setSelectedFormIDs([...selectedFormIDs, formID]);
            }
          }}
          onSelectAll={() => {
            if (!allSelected) {
              setSelectedFormIDs(allForms.map((f) => f.id));
            } else {
              setSelectedFormIDs([]);
            }
            setAllSelected(!allSelected);
          }}
          allForms={allForms}
          hasFullAccess={formSubmissionsAccess?.all}
          selectedFormSubmissionIDs={selectedFormIDs}
        />
      </div>
      <DialogActionsWrapper>
        <Button
          variant="ghost"
          onClick={() => {
            setFormIDsPopulated(false);
            onClose();
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => {
            onSave(selectedFormIDs);
            setFormIDsPopulated(false);
            formSubmissionsAccess.form_ids = selectedFormIDs;
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActionsWrapper>
    </Dialog>
  );
}
